import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, errMsg, emailNote, useFetch } from "@/lib/api";
import { fmtDate, gbp, toISODate, QUOTE_STATUSES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, Loading, ErrorBox, Section, StatusBadge, DemoTag, NativeSelect, Field } from "@/components/Common";
import { SimpleTable } from "@/components/SimpleTable";
import { QuoteView } from "@/components/QuoteView";
import { ActivityList } from "@/components/Activity";

export function Quotations() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState(params.get("status") || "");
  const [q, setQ] = useState("");
  const { data, loading, error } = useFetch(`/admin/quotations?status=${encodeURIComponent(status)}&q=${encodeURIComponent(q)}`);
  return (
    <div>
      <PageHeader title="Quotations" actions={<Button onClick={() => nav("/admin/quotations/new")} data-testid="new-quote-btn"><Plus className="mr-1 h-4 w-4" />New quotation</Button>} />
      <div className="mb-4 grid max-w-2xl gap-2 sm:grid-cols-2">
        <Input placeholder="Search reference or customer" aria-label="Search quotations" value={q} onChange={(e) => setQ(e.target.value)} data-testid="quotes-search-input" />
        <NativeSelect aria-label="Status filter" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="quotes-status-filter"><option value="">All statuses</option>{QUOTE_STATUSES.map((s) => <option key={s}>{s}</option>)}</NativeSelect>
      </div>
      <ErrorBox error={error} />
      {loading && !data ? <Loading /> : (
        <SimpleTable testId="quotes-table" rows={data || []} onRow={(x) => nav(`/admin/quotations/${x.id}`)} rowTestId={(x) => `quote-row-${x.reference}`}
          cols={[
            { label: "Reference", render: (x) => <span className="font-medium">{x.reference}<DemoTag show={x.is_demo} /></span> },
            { label: "Customer", render: (x) => x.customer_name },
            { label: "Request", render: (x) => x.request_reference },
            { label: "Total", render: (x) => gbp(x.total) },
            { label: "Expires", render: (x) => fmtDate(x.expiry_date) },
            { label: "Status", render: (x) => <StatusBadge status={x.status} /> },
          ]} />
      )}
    </div>
  );
}

const blankItem = () => ({ description: "", quantity: 1, unit_price: "" });

function QuoteForm({ initial, settings, requests, onSaved }) {
  const today = new Date();
  const exp = new Date(); exp.setDate(exp.getDate() + settings.quote_validity_days);
  const [f, setF] = useState(initial || { request_id: "", description: "", items: [blankItem()], discount: "0", vat_applied: settings.vat_registered,
    issue_date: toISODate(today), expiry_date: toISODate(exp), terms: settings.default_terms, notes: "" });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const setItem = (i, k, v) => setF({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) });
  const sub = f.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const net = sub - (Number(f.discount) || 0);
  const vat = f.vat_applied && settings.vat_registered ? (net * Number(settings.vat_rate)) / 100 : 0;
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const body = { ...f, items: f.items.map((it) => ({ ...it, quantity: Number(it.quantity), unit_price: String(it.unit_price || "0") })) };
      const r = initial?.id ? await api.put(`/admin/quotations/${initial.id}`, body) : await api.post("/admin/quotations", body);
      toast.success("Draft saved - totals calculated by the server");
      onSaved(r.data);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Section>
      <div className="grid gap-4">
        {!initial?.id && (
          <Field label="Job request" id="qr" required>
            <NativeSelect id="qr" value={f.request_id} onChange={(e) => setF({ ...f, request_id: e.target.value })} data-testid="quote-request-select">
              <option value="">Select…</option>{requests.map((r) => <option key={r.id} value={r.id}>{r.reference} - {r.customer_name} ({r.property.postcode})</option>)}
            </NativeSelect>
          </Field>
        )}
        <Field label="Description of proposed work" id="qd" required><Textarea id="qd" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="quote-description-input" /></Field>
        <div>
          <p className="mb-2 text-sm font-medium">Line items</p>
          <div className="space-y-2">
            {f.items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2" data-testid={`quote-item-${i}`}>
                <Input className="col-span-12 sm:col-span-6" placeholder="Description" aria-label={`Item ${i + 1} description`} value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`quote-item-desc-${i}`} />
                <Input className="col-span-4 sm:col-span-2" type="number" min="0" step="0.01" aria-label={`Item ${i + 1} quantity`} value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`quote-item-qty-${i}`} />
                <Input className="col-span-5 sm:col-span-3" type="number" min="0" step="0.01" placeholder="Unit £" aria-label={`Item ${i + 1} unit price`} value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`quote-item-price-${i}`} />
                <Button className="col-span-3 sm:col-span-1" variant="ghost" size="icon" aria-label="Remove item" onClick={() => setF({ ...f, items: f.items.filter((_, j) => j !== i) })} disabled={f.items.length === 1}><Trash2 className="h-4 w-4" /></Button>
              </div>))}
          </div>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => setF({ ...f, items: [...f.items, blankItem()] })} data-testid="quote-add-item-btn"><Plus className="mr-1 h-4 w-4" />Add item</Button>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Discount (£)" id="qdisc"><Input id="qdisc" type="number" min="0" step="0.01" value={f.discount} onChange={(e) => setF({ ...f, discount: e.target.value })} data-testid="quote-discount-input" /></Field>
          <Field label="Issue date" id="qi"><Input id="qi" type="date" value={f.issue_date} onChange={(e) => setF({ ...f, issue_date: e.target.value })} data-testid="quote-issue-input" /></Field>
          <Field label="Expiry date" id="qe"><Input id="qe" type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} data-testid="quote-expiry-input" /></Field>
        </div>
        {settings.vat_registered ? (
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.vat_applied} onCheckedChange={(v) => setF({ ...f, vat_applied: !!v })} data-testid="quote-vat-checkbox" /> Charge VAT at {Number(settings.vat_rate)}%</label>
        ) : <p className="text-sm text-muted-foreground" data-testid="quote-vat-note">VAT is not charged - the business is set as not VAT registered (change in Settings).</p>}
        <Field label="Terms and conditions" id="qt"><Textarea id="qt" rows={3} value={f.terms} onChange={(e) => setF({ ...f, terms: e.target.value })} data-testid="quote-terms-input" /></Field>
        <Field label="Additional notes (visible to customer)" id="qn"><Textarea id="qn" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="quote-notes-input" /></Field>
        <div className="rounded-md bg-muted p-3 text-sm" data-testid="quote-preview-totals">
          Estimated: subtotal {gbp(sub)} · net {gbp(net)}{vat ? ` · VAT ${gbp(vat)}` : ""} · <strong>total {gbp(net + vat)}</strong>
          <span className="block text-xs text-muted-foreground">Final totals are calculated on the server when saved.</span>
        </div>
        <ErrorBox error={err} />
        <Button className="w-fit" onClick={save} disabled={busy} data-testid="quote-save-btn">{busy ? "Saving…" : "Save draft"}</Button>
      </div>
    </Section>
  );
}

export function QuotationEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { data: settings } = useFetch("/admin/settings");
  const { data: requests } = useFetch("/admin/requests");
  const [q, setQ] = useState(null);
  const [edit, setEdit] = useState(false);
  const [err, setErr] = useState(null);
  const load = () => id && api.get(`/admin/quotations/${id}`).then((r) => setQ(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <ErrorBox error={err} />;
  if (!settings || !requests || (id && !q)) return <Loading />;
  const send = async () => {
    if (!window.confirm(`Issue ${q.reference} to ${q.customer_name}? It can no longer be edited.`)) return;
    try { const r = await api.post(`/admin/quotations/${id}/send`); toast.success(`Quotation issued to customer portal. ${emailNote(r.data.email_status)}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const convert = async () => {
    try { const r = await api.post(`/admin/quotations/${id}/convert`); toast.success(`Job ${r.data.reference} created`); nav(`/admin/jobs/${r.data.id}`); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!id) {
    const pre = params.get("request");
    return (
      <div className="mx-auto max-w-4xl">
        <PageHeader title="New quotation" />
        <QuoteForm settings={settings} requests={requests.filter((r) => !["Closed", "Converted to Job"].includes(r.status))}
          initial={pre ? { request_id: pre, description: "", items: [blankItem()], discount: "0", vat_applied: settings.vat_registered, issue_date: toISODate(new Date()),
            expiry_date: toISODate(new Date(Date.now() + settings.quote_validity_days * 864e5)), terms: settings.default_terms, notes: "" } : null}
          onSaved={(d) => nav(`/admin/quotations/${d.id}`, { replace: true })} />
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageHeader title={<>Quotation {q.reference}<DemoTag show={q.is_demo} /></>} subtitle={<>Customer {q.customer_name} · <Link className="underline" to={`/admin/requests/${q.request_id}`}>{q.request_reference}</Link></>}
        actions={<>
          {q.status === "Draft" && !edit && <Button variant="outline" onClick={() => setEdit(true)} data-testid="quote-edit-btn">Edit draft</Button>}
          {q.status === "Draft" && !edit && <Button onClick={send} data-testid="quote-send-btn">Issue to customer</Button>}
          {q.status === "Accepted" && <Button onClick={convert} data-testid="quote-convert-btn">Convert to job</Button>}
        </>} />
      {edit ? <QuoteForm initial={q} settings={settings} requests={[]} onSaved={() => { setEdit(false); load(); }} /> : <Section><QuoteView q={q} /></Section>}
      <Section title="Activity"><ActivityList items={q.activity} /></Section>
    </div>
  );
}
